import s from './PostHeader.module.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { PostHeaderProps } from '$shared/components/Post/PostHeader/PostHeader.interface';
import { UnitName, UnitPhoto, Verified } from '$shared/components';
import { Link, useRoute } from 'react-router5';
import { IcChart28, IcEdit28, IcMore20, IcPin24, IcReport24, IcUnpin28 } from '$assets';
import { AuthPopoverState, PostType, SubscriptionType } from '$store/models';
import { ActionBottomSheet, Button, Label } from '$uikit';
import {
  useCurrentUserInfo,
  useDate,
  useGroupInfo,
  usePost,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { GROUP, PROFILE, RESOLVE } from '$shared/constants/pages';
import { useDispatch, useSelector } from 'react-redux';
import { authSelector } from '$store/auth';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';
import { ReportReason } from '$shared/constants/reportReason';
import { profileActions } from '$store/profile';
import { postsActions } from '$store/posts';
import { showModal } from '$navigation/helper';
import { classNames, isTesterId } from '$utils';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import { mainSelector } from '$store/main';

enum PostActionsGroups {
  REPORT = 'report',
}

export const PostHeader: FC<PostHeaderProps> = (props) => {
  const {
    postId,
    showFollowBtn,
    isFeed,
    sourceLabel,
    isFollowed,
    isRecommendations,
    isScheduled,
    isFullPage,
    isShareButtonHidden,
    handleRepost,
    isVisible,
  } = props;
  const [isMenuShown, setMenuShown] = useState(false);
  const [displayedTipsAnimation, setDisplayedTipsAnimation] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const timeout = setTimeout(() => {
        setDisplayedTipsAnimation(true);
      }, 4200);
      return () => {
        clearTimeout(timeout);
      };
    } else {
      setDisplayedTipsAnimation(false);
    }
  }, [isVisible]);

  const dispatch = useDispatch();
  const { route } = useRoute();
  const currentUser = useCurrentUserInfo();
  const t = useTranslator();
  const { isLogged } = useSelector(authSelector);
  const post = usePost(postId);
  const date = useDate(post.createdAt);
  const user = useUserInfo(post.publisherId);
  const group = useGroupInfo(-post.ownerId);
  const isProfilePage = route.name === PROFILE || route.name === RESOLVE;
  const isAds = post.adId > 0;
  const canDelete = post.canDelete || group?.isModer;
  const isAuthor = post.ownerId === currentUser.id;

  const { decryptKeys } = useSelector(mainSelector);
  const canWrite = !!decryptKeys[post.ownerId];

  const isTester = useMemo(() => {
    if (!currentUser) {
      return false;
    }

    return isTesterId(currentUser.id);
  }, [currentUser]);

  const handleProfileNameClick = useCallback((event) => {
    event.stopPropagation();
    const scrollViewElement = document.querySelector('._ScrollView');
    scrollViewElement && scrollViewElement.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const authorInfo = useMemo(() => {
    if (post.ownerId < 0) {
      return group
        ? {
            photo: group.photo,
            route: group.domain ? RESOLVE : GROUP,
            routeParams: group.domain ? { domain: group.domain } : { id: group.id },
          }
        : null;
    } else {
      return user
        ? {
            hasNftPhoto: user.hasNftPhoto,
            photo: user.photo,
            route: user.domain ? RESOLVE : PROFILE,
            routeParams: user.domain ? { domain: user.domain } : { id: user.id },
          }
        : null;
    }
  }, [post.ownerId, group, user]);

  const actions = useMemo(() => {
    const now = Date.now() / 1000;
    const result: ActionBottomSheetOption[] = [];

    if (isTester) {
      result.push({ label: 'Score', value: 'score', icon: <IcChart28 /> });
    }

    if (now - post.createdAt < 86400 && canDelete) {
      result.push({ label: t('post_action_edit'), value: 'edit', icon: <IcEdit28 /> });
    }

    if (canDelete && !post.pinned && !isScheduled) {
      result.push({ label: t('post_action_pin'), value: 'pin', icon: <IcPin24 /> });
    }

    if (canDelete && post.pinned && !isScheduled) {
      result.push({ label: t('post_action_unpin'), value: 'unpin', icon: <IcUnpin28 /> });
    }

    if (!isAuthor) {
      result.push({
        label: t('post_action_report'),
        value: 'report',
        icon: <IcReport24 />,
        options: [
          {
            label: t('post_action_report_spam'),
            value: ReportReason.SPAM,
            groupId: PostActionsGroups.REPORT,
          },
          {
            label: t('post_action_report_offensive'),
            value: ReportReason.OFFENSIVE,
            groupId: PostActionsGroups.REPORT,
          },
          {
            label: t('post_action_report_false_info'),
            value: ReportReason.FALSE_INFO,
            groupId: PostActionsGroups.REPORT,
          },
        ],
      });
    }

    if (canDelete) {
      result.push({ label: t('post_action_delete'), value: 'delete', mode: 'alert' });
    }

    if (isRecommendations) {
      result.push({ label: t('post_action_hide_author'), value: 'hideAuthor' });
    }

    if (!isFullPage && isShareButtonHidden) {
      result.push({ label: t('post_share_title'), value: 'repost' });
    }

    return result;
  }, [
    post.createdAt,
    canDelete,
    post.pinned,
    isTester,
    t,
    isRecommendations,
    isShareButtonHidden,
    isFullPage,
  ]);

  const handleMenu = useCallback(
    (value) => {
      setMenuShown(false);

      if (!isLogged) {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      }

      if (value === 'score') {
        dispatch(toastActions.setToastLoading());
        Api.get(`/posts/${postId}/score`)
          .then((resp) => {
            dispatch(toastActions.hideToast());

            const data: any = resp;
            let items: string[] = [];
            for (let k in data.result) {
              items.push(`${k}: ${data.result[k]}`);
            }

            alert(items.join('\n'));
          })
          .catch((err) => {
            dispatch(toastActions.setToastFail(err.message));
          });
      } else if (value === 'delete') {
        dispatch(postsActions.deletePost(postId));
      } else if (value === 'edit') {
        showModal(`edit_post${postId}`);
      } else if (value === 'pin') {
        dispatch(postsActions.pinPost(postId));
      } else if (value === 'unpin') {
        dispatch(postsActions.unpinPost(postId));
      } else if (value === 'hideAuthor') {
        dispatch(postsActions.hideAuthor(post.ownerId));
      } else if (Object.values(ReportReason).includes(value)) {
        dispatch(
          postsActions.reportPost({
            postId,
            reason: value,
          }),
        );
      } else if (value === 'repost') {
        handleRepost();
      } else if (value === 'promote') {
        showModal(`promote${postId}`, false, {
          target: 'post',
        });
      }
    },
    [postId, post, isLogged],
  );

  const handleShowMenu = useCallback((e) => {
    e.stopPropagation();
    setMenuShown(true);
  }, []);

  const activeSubscriber = Object.keys(decryptKeys).length > 1;
  const freeWriteBadgeVisible =
    !canWrite && !!user?.allowWriteForFree && isFeed && !activeSubscriber;

  return (
    <>
      <div className="Post__header">
        <div className="Post__header__left">
          <UnitPhoto
            unit={post.ownerId < 0 ? group : user}
            size="small"
            onClick={isProfilePage ? handleProfileNameClick : undefined}
          />
          {!!authorInfo && (
            <Link
              routeName={authorInfo.route}
              routeParams={authorInfo.routeParams}
              onClick={isProfilePage ? handleProfileNameClick : undefined}
            >
              <div className="Post__header__content">
                <div className="Post__header__name_wrap">
                  <div className="Post__header__name">
                    <UnitName unit={user} />
                  </div>
                  <Verified objectId={post.ownerId} />
                </div>
                <div className="Post__header__date_wrap">
                  <div className="Post__header__date">
                    {isAds && isFeed ? t('post_sponsored') : showFollowBtn ? null : date}
                  </div>
                  {post.pinned && !isFeed && (
                    <div className="Post__header__pinned">
                      <IcPin24 />
                    </div>
                  )}
                  {post.type === PostType.PRIVATE_SUBSCRIPTION ||
                  (post.type === PostType.SUBSCRIPTION && post.isAdult) ? (
                    <div className="Post__header__label">
                      <Label
                        type={
                          post.type === PostType.SUBSCRIPTION
                            ? user?.verified === 2
                              ? SubscriptionType.Adult
                              : SubscriptionType.Media
                            : SubscriptionType.Author
                        }
                      />
                    </div>
                  ) : freeWriteBadgeVisible ? (
                    <div className={s.freeWriteBadge}>{t('post_free_write_badge')}</div>
                  ) : null}
                  {sourceLabel && <div className="Post__source">{sourceLabel}</div>}
                </div>
              </div>
            </Link>
          )}
        </div>
        <div className="Post__header__right">
          {!freeWriteBadgeVisible && showFollowBtn && !isAds && (
            <div className="Post__header__follow">
              {isFollowed && post.isAdult ? (
                <Button
                  className={classNames('Post__tipsAnimation', {
                    flare: displayedTipsAnimation && post.isAdult,
                  })}
                  size="small"
                  mode="quinary"
                >
                  {t('post_message')}
                </Button>
              ) : (
                <Button size="small" mode="quinary">
                  {t(isFollowed ? 'post_unfollow' : 'post_follow')}
                </Button>
              )}
            </div>
          )}
          {actions.length > 0 && isLogged && (
            <div className="Post__header__more" onClick={handleShowMenu}>
              <IcMore20 />
            </div>
          )}
        </div>
      </div>
      <ActionBottomSheet
        title={t('post_actions_title')}
        isOpened={isMenuShown}
        options={actions}
        onChange={handleMenu}
        onClose={() => setMenuShown(false)}
      />
    </>
  );
};
